"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PinContainer } from "@/components/ui/3d-pin";
import { Plus, Sparkles, Layout, ArrowRight, ChevronUp } from "lucide-react";

const INITIAL_VISIBLE = 6;
import Loader from "@/components/Loader";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchServices, fetchProjects } from "@/store/slices/landingSlice";

import { imageUrl } from "@/services/BaseUrl";
import type { Project } from "@/types/landing";

interface PortfolioSectionProps {
  className?: string;
}

const PortfolioSection: React.FC<PortfolioSectionProps> = ({
  className = "",
}) => {
  const dispatch = useAppDispatch();
  const { data: services } = useAppSelector((state) => state.landing.services);
  const { data: projects, loading: projectsLoading } = useAppSelector((state) => state.landing.projects);
  const { mode } = useAppSelector((state) => state.theme);
  const servicesLoading = useAppSelector((state) => state.landing.services.loading);

  const [selectedServiceId, setSelectedServiceId] = useState<string>("All");
  const [showAll, setShowAll] = useState(false);

  const loading = projectsLoading || servicesLoading;
  const isInitialLoading =
    (servicesLoading && services.length === 0) ||
    (projectsLoading && projects.length === 0);

  useEffect(() => {
    dispatch(fetchServices());
  }, [dispatch]);

  useEffect(() => {
    setShowAll(false);
    if (selectedServiceId === "All") {
      dispatch(fetchProjects({ page: 1, limit: 1000 }));
    } else {
      dispatch(fetchProjects({ page: 1, limit: 1000, serviceId: selectedServiceId }));
    }
  }, [selectedServiceId]);

  const toIdStr = (v: unknown): string => {
    if (!v) return "";
    if (typeof v === "object") {
      const obj = v as Record<string, unknown>;
      return String(obj._id ?? obj.id ?? "").trim();
    }
    return String(v).trim();
  };

  const filteredProjects = selectedServiceId === "All"
    ? projects
    : projects.filter((project) => {
        if (!project.serviceId) return false;
        return toIdStr(project.serviceId) === selectedServiceId.trim();
      });

  const visibleProjects = showAll ? filteredProjects : filteredProjects.slice(0, INITIAL_VISIBLE);
  const hasMore = filteredProjects.length > INITIAL_VISIBLE;

  const getProjectImageUrl = (img?: string): string | null => {
    if (!img || typeof img !== "string") return null;
    const trimmed = img.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      return trimmed;
    }
    const cleanPath = trimmed.replace(/\\/g, "/").replace(/^\/+/, "");
    return `${imageUrl}/${cleanPath}`;
  };

  const getServiceTitle = (project: Project): string => {
    if (typeof project.serviceId === 'object' && project.serviceId) {
      return project.serviceId.title || "Uncategorized";
    }
    if (typeof project.serviceId === 'string' && project.serviceId) {
      const matched = services.find(
        (s) => (s._id || (s as any).id) === project.serviceId
      );
      return matched?.title || "Uncategorized";
    }
    return "Uncategorized";
  };

  const getProjectGradient = (index: number): string => {
    const gradients = [
      "from-violet-500 via-purple-500 to-indigo-500",
      "from-blue-500 via-cyan-500 to-teal-500",
      "from-green-500 via-emerald-500 to-teal-500",
      "from-orange-500 via-red-500 to-pink-500",
      "from-pink-500 via-rose-500 to-red-500",
      "from-indigo-500 via-blue-500 to-cyan-500",
    ];
    return gradients[index % gradients.length];
  };

  if (isInitialLoading) {
    return (
      <section className={`py-24 relative overflow-hidden transition-colors duration-700 ${mode === "dark" ? "bg-[#0B0F19]" : "bg-slate-50"} ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Loader loading={loading} services={services} mode={mode} className="min-h-[400px]" />
        </div>
      </section>
    );
  }

  return (
    <section
      className={`py-24 pb-48 relative overflow-hidden transition-colors duration-700 ${mode === "dark" ? "bg-[#0B0F19]" : "bg-slate-50"} ${className}`}
    >
      {/* Background Ambience */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute top-0 -left-64 w-[500px] h-[500px] rounded-full blur-[100px] transition-all duration-700 ${mode === "dark" ? "bg-[#8B31FF]/20 opacity-100" : "bg-[#8B31FF]/10 opacity-50"}`}
        />
        <div
          className={`absolute bottom-0 -right-64 w-[500px] h-[500px] rounded-full blur-[100px] transition-all duration-700 ${mode === "dark" ? "bg-[#2B00A4]/20 opacity-100" : "bg-[#2B00A4]/10 opacity-50"}`}
        />
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${mode === "dark" ? "opacity-[0.03]" : "opacity-[0.05]"}`}
          style={{
            backgroundImage:
              mode === "dark"
                ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
                : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className={`inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full border backdrop-blur-md transition-all duration-300 ${
              mode === "dark"
                ? "border-[#8B31FF]/30 bg-[#8B31FF]/10"
                : "border-purple-100 bg-purple-50"
            }`}
          >
            <Sparkles
              className={`w-4 h-4 ${mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
            />
            <span
              className={`font-semibold text-xs uppercase tracking-wider transition-colors duration-300 ${mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
            >
              Our Masterpieces
            </span>
          </motion.div>

          <h2
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.12] ${mode === "dark" ? "text-white" : "text-gray-900"}`}
          >
            Selected{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">
              Works
            </span>
          </h2>

          <p
            className={`text-base sm:text-lg max-w-2xl mx-auto leading-relaxed transition-colors duration-300 ${mode === "dark" ? "text-gray-400" : "text-gray-600"}`}
          >
            Explore our portfolio of award-winning projects, where innovation
            meets exceptional design.
          </p>
        </motion.div>

        {/* Service Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-16"
        >
          <div className="relative">
            <div
              className={`pointer-events-none absolute left-0 top-0 h-full w-8 z-10 md:hidden bg-gradient-to-r ${mode === "dark" ? "from-[#0B0F19]" : "from-slate-50"} to-transparent`}
            />
            <div
              className={`pointer-events-none absolute right-0 top-0 h-full w-8 z-10 md:hidden bg-gradient-to-l ${mode === "dark" ? "from-[#0B0F19]" : "from-slate-50"} to-transparent`}
            />

            <div
              className="flex md:flex-wrap md:justify-center gap-3 overflow-x-auto md:overflow-x-visible px-4 md:px-0 pb-1 md:pb-0 scroll-smooth"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              <button
                onClick={() => setSelectedServiceId("All")}
                className={`relative flex-shrink-0 px-5 py-2.5 rounded-lg cursor-pointer font-semibold text-sm transition-all duration-300 border whitespace-nowrap ${
                  selectedServiceId === "All"
                    ? mode === "dark"
                      ? "bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.25)] border-white"
                      : "bg-[#8B31FF] text-white shadow-[0_4px_14px_rgba(79,17,194,0.4)] border-[#8B31FF]"
                    : mode === "dark"
                      ? "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border-white/10"
                      : "bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-800 border-gray-200 shadow-sm"
                }`}
              >
                All Projects
              </button>

              {services.map((service, index) => {
                const id = service._id || service.id || "";
                const isActive = selectedServiceId === id;
                return (
                  <button
                    key={id || index}
                    onClick={() => setSelectedServiceId(id)}
                    className={`relative flex-shrink-0 rounded-lg px-5 py-2.5 rounded-full cursor-pointer font-semibold text-sm transition-all duration-300 border whitespace-nowrap ${
                      isActive
                        ? mode === "dark"
                          ? "bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.25)] border-white"
                          : "bg-[#8B31FF] text-white shadow-[0_4px_14px_rgba(79,17,194,0.4)] border-[#8B31FF]"
                        : mode === "dark"
                          ? "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border-white/10"
                          : "bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-800 border-gray-200 shadow-sm"
                    }`}
                  >
                    {service.title}
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Portfolio Grid — now 3D pin cards */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedServiceId}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-24 gap-x-6 md:gap-x-8 xl:gap-x-10 pt-10"
          >
            {visibleProjects.map((item, index) => (
              <motion.div
                key={item._id || item.id || index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{
                  duration: 0.4,
                  ease: "easeOut",
                  delay: Math.min(index % 6, 5) * 0.08,
                }}
                layout
                className="w-full flex items-center justify-center relative isolate z-0 h-[24rem]"
              >
                <PinContainer
                  title={item.liveUrl ? "Live Preview" : getServiceTitle(item)}
                  href={item.liveUrl || "#"}
                  mode={mode}
                  containerClassName="w-full h-full"
                  className="w-[20rem] h-[24rem]"
                >
                  <div className="flex flex-col p-4 tracking-tight w-[20rem] h-[24rem]">
                    <h3
                      className={`max-w-xs !pb-2 !m-0 font-bold text-base transition-colors duration-300 ${mode === "dark" ? "text-slate-100" : "text-gray-900"}`}
                    >
                      {item.title}
                    </h3>
                    <div className="text-base !m-0 !p-0 font-normal">
                      <span
                        className={`transition-colors duration-300 ${mode === "dark" ? "text-slate-500" : "text-gray-500"}`}
                      >
                        {item.description}
                      </span>
                    </div>

                    {/* Project Image or Gradient */}
                    <div className="flex flex-1 w-full rounded-lg mt-4 overflow-hidden relative group">
                      {item.images &&
                      item.images.length > 0 &&
                      getProjectImageUrl(item.images[0]) ? (
                        <>
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={getProjectImageUrl(item.images[0]) || ""}
                            alt={item.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                            }}
                          />
                          <div
                            className={`absolute inset-0 w-full h-full bg-gradient-to-br ${getProjectGradient(index)} opacity-0`}
                          >
                            <div
                              className="absolute inset-0 opacity-20"
                              style={{
                                backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)`,
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <div
                          className={`w-full h-full bg-gradient-to-br ${getProjectGradient(index)} opacity-80`}
                        >
                          <div
                            className="absolute inset-0 opacity-20"
                            style={{
                              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)`,
                            }}
                          />
                        </div>
                      )}

                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <Plus className="w-12 h-12 text-white" />
                      </div>

                      <div className="absolute top-4 left-4 px-3 py-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-xs font-semibold text-white">
                        {getServiceTitle(item)}
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mt-4">
                      {item.tags?.slice(0, 4).map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className={`px-3 py-1 rounded-full text-[10px] font-medium border transition-colors duration-300 ${
                            mode === "dark"
                              ? "bg-gray-700/50 text-gray-300 border-gray-600"
                              : "bg-gray-100 text-gray-600 border-gray-200"
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </PinContainer>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* View All / Show Less button */}
        {hasMore && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-center mt-20"
          >
            <button
              onClick={() => setShowAll((prev) => !prev)}
              className={`group relative cursor-pointer inline-flex items-center gap-3 px-8 py-4 rounded-lg font-bold text-base overflow-hidden transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 ${
                mode === "dark"
                  ? "bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] text-white shadow-[#8B31FF]/30"
                  : "bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] text-white shadow-purple-300"
              }`}
            >
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              {showAll ? (
                <>
                  <ChevronUp size={20} className="relative z-10" />
                  <span className="relative z-10">Show Less</span>
                </>
              ) : (
                <>
                  <span className="relative z-10">View All Projects</span>
                  <ArrowRight
                    size={20}
                    className="relative z-10 group-hover:translate-x-1 transition-transform"
                  />
                </>
              )}
            </button>
          </motion.div>
        )}

        {/* No Results Message */}
        {filteredProjects.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-20 text-center"
          >
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 transition-colors duration-300 ${mode === "dark" ? "bg-[#2B00A4]/30" : "bg-purple-100"}`}
            >
              <Layout
                className={`w-8 h-8 transition-colors duration-300 ${mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
              />
            </div>
            <h3
              className={`text-xl font-semibold mb-2 transition-colors duration-300 ${mode === "dark" ? "text-white" : "text-gray-900"}`}
            >
              No projects found
            </h3>
            <p
              className={`transition-colors duration-300 ${mode === "dark" ? "text-gray-400" : "text-gray-600"}`}
            >
              There are no projects in this category yet.
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default PortfolioSection;